import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import React, { useEffect, useState } from "react";

import * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { int0 } from "../format";
import type { PlaceEditActions } from "./useOzonDocActions";

/** Сколько ждать после последнего нажатия. Меньше — снова запрос на букву, больше — экран
 *  ощутимо «думает» уже после того, как человек дописал слово. */
export const SEARCH_DELAY_MS = 350;

/** Короче — не спрашиваем: одна буква совпадает у половины каталога, и тридцать случайных
 *  строк не ответ. Пустой поиск при этом разрешён — это начальный список. */
export const SEARCH_MIN = 2;

/** Чем поле поиска отличается от того, что реально спрошено у 1С. */
export interface CatalogSearch {
  /** отлежавшееся слово — им и спрашивают; оно же в ключе запроса */
  term: string;
  /** слово короче `SEARCH_MIN` — не спрашиваем вовсе и говорим об этом */
  tooShort: boolean;
  /** поле уже не то, по чему получен список: ждём конца отлёжки */
  behind: boolean;
}

/** Поле поиска → то, что спрашивают у 1С. Отдельно от разметки, чтобы отлёжку можно было
 *  проверить тестом: без неё ключ запроса менялся на каждом нажатии клавиши. */
export function useCatalogSearch(search: string): CatalogSearch {
  const [term, setTerm] = useState(search.trim());
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [search]);
  const tooShort = term.length > 0 && term.length < SEARCH_MIN;
  return { term, tooShort, behind: !tooShort && search.trim() !== term };
}

/** «Добавить артикул…» — единственный путь, которым состав РАСТЁТ после заявки.
 *
 *  ⚠️ Каталог спрашивается у живой 1С тем же путём, что и само добавление: показать товар,
 *  которого добавление потом не найдёт, значит соврать. Увеличить СУММАРНОЕ количество заявки
 *  площадка не даёт (`INBOUND_NO_CAPACITY`) — об этом говорит превью, и сказать это здесь тоже
 *  обязаны: иначе правка делается ради отказа на шаге состава.
 *
 *  ⚠️ Поиск ОТЛЁЖИВАЕТСЯ (`SEARCH_DELAY_MS`) и коротких слов не спрашивает: каждый запрос — это
 *  поход в живую 1С за всем каталогом МП, и ключ по каждому нажатию клавиши слал их пачкой.
 *  Фильтровать на клиенте нельзя: сервер отбирает ПО ПОДСТРОКЕ и режет выдачу до 30 строк
 *  (`CATALOG_LIMIT`), то есть один запрос с пустым поиском отдал бы тридцать первых по алфавиту
 *  карточек из тысяч — искать в них было бы поиском не там.
 */
export function ChangeAddItem({
  shipmentId,
  busy,
  edits,
}: {
  shipmentId: number;
  busy: boolean;
  edits: PlaceEditActions;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pick, setPick] = useState<ozon.OzonCatalogItem | null>(null);
  const [qty, setQty] = useState("");
  const [kind, setKind] = useState("BOX");
  // ⚠️ спрашиваем по ОТЛЕЖАВШЕМУСЯ слову, а не по тому, что в поле: иначе ключ меняется на
  // каждом нажатии, и живая 1С получает запрос на букву
  const { term, tooShort, behind } = useCatalogSearch(search);
  const q = useQuery({
    queryKey: ["supply", "ozon", "change-catalog", shipmentId, term],
    queryFn: () => ozon.getOzonChangeCatalog(shipmentId, term),
    enabled: open && !tooShort,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const n = Number(qty);
  const bad = !Number.isInteger(n) || n <= 0;
  const close = (): void => {
    setOpen(false);
    setPick(null);
    setQty("");
  };
  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setPick(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" disabled={busy}>
          <Plus className="size-4" /> Добавить артикул…
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-2 text-xs">
        <input
          value={search}
          autoFocus
          onChange={(e) => setSearch(e.target.value)}
          placeholder="артикул или название"
          className="mb-1.5 h-7 w-full rounded border border-input bg-background px-2"
        />
        {q.isError && (
          <div className="mb-1 rounded border border-red-300 bg-red-50 px-2 py-1 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            ⚠ {extractErrorMessage(q.error, "Каталог 1С не ответил")}
          </div>
        )}
        {tooShort && (
          <div className="text-muted-foreground">
            Допишите слово: по одной букве каталог не ищем — совпадёт половина.
          </div>
        )}
        {(q.isFetching || behind) && (
          <div className="text-muted-foreground">Ищем в 1С…</div>
        )}
        {q.data?.length === 0 && !q.isFetching && !tooShort && (
          <div className="text-muted-foreground">
            Ничего не нашлось. Ищем по карточкам 1С с заполненным SKU Ozon.
          </div>
        )}
        <div className="max-h-52 space-y-0.5 overflow-y-auto">
          {(tooShort ? [] : (q.data ?? [])).map((c) => (
            <button
              key={c.mp_sku}
              type="button"
              onClick={() => setPick(c)}
              className={cn(
                "flex w-full items-start gap-2 rounded px-2 py-1 text-left hover:bg-accent",
                pick?.mp_sku === c.mp_sku && "bg-accent",
              )}
              title={`SKU площадки ${c.mp_sku}`}
            >
              <span className="w-24 shrink-0 font-medium">{c.article}</span>
              <span className="min-w-0 flex-1 truncate">{c.name}</span>
              {/* ⚠️ Без кванта место не посчитать: бэкенд такой товар не добавит, и знать об
                  этом надо ДО выбора количества */}
              <span className="shrink-0 text-muted-foreground">
                {c.in_box === null
                  ? "нет кванта"
                  : `в коробе ${int0(c.in_box)}`}
              </span>
            </button>
          ))}
        </div>

        {pick && (
          <div className="mt-1.5 space-y-1.5 border-t pt-1.5">
            <div className="font-medium">
              {pick.article} — {pick.name}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="штук"
                className="h-7 w-24 rounded border border-input bg-background px-2"
              />
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="h-7 rounded border border-input bg-background px-1"
                title="Тип НОВОГО места: класть в место с кодом нельзя ничем"
              >
                <option value="BOX">новым коробом</option>
                <option value="PALLET">новой паллетой</option>
              </select>
              <Button
                size="sm"
                disabled={busy || bad}
                onClick={() => {
                  edits.add.mutate({ mp_sku: pick.mp_sku, qty: n, kind });
                  close();
                }}
              >
                Добавить
              </Button>
            </div>
            <div className="text-muted-foreground">
              Партию портал подберёт сам (FEFO) — заменить её можно тут же, в
              строке места. Суммарное количество заявки увеличить площадка не
              даёт: убавьте столько же на другой позиции.
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
